import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination, usePageSize, usePaged } from "@/components/Pagination";
import { useStickySearch } from "@/lib/stickySearch";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fetchAll } from "@/lib/apiClient";
import { ALL_TIME, DateRange, isoToday, rangeLabel, useDateRange, withinRange } from "@/components/DateRange";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill, pillLabel } from "@/components/StatusPill";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Search, ArrowUp, ArrowDown, ArrowUpDown, MoreVertical, MessageSquare, MessageCircle, FileText, Truck, Copy, X } from "lucide-react";
import { nextStates } from "@/lib/lifecycle";
import { ADMIN_TXN_COLUMNS, ADMIN_TXN_GRID_COLS, formatShortDate, formatRelativeTime, ADMIN_TXN_SORT_ACCESSORS, PENDING_CUSTOMER_COLUMNS, PENDING_CUSTOMER_GRID_COLS, formatPendingDuration, PENDING_ADMIN_COLUMNS, PENDING_ADMIN_GRID_COLS, softcopyIconTone, deliveryIconTone, PENDING_REVIEWER_COLUMNS, PENDING_REVIEWER_GRID_COLS, COMPLETED_COLUMNS, COMPLETED_GRID_COLS, getClosingNote, txnGates, gateReason, adminTxnMatchesSearch, TXN_QUEUES, DEFAULT_TXN_QUEUE, TxnIdCell, ActionBadges, AssignMenu, CustomerCell, PendingAtCell, InvoiceCell, unreadRowClass, msgIconClass, buildTrackingUrl, pinnedFirst, usePinToggle } from "./lib";
import { expandSection } from "@/components/CollapsibleSection";

// How often the queue asks whether anything changed. 15s is under the threshold at which a page
// stops feeling live, and the request it makes is two aggregates over an indexed column — small
// enough that shortening it further would buy responsiveness nobody would notice at the cost of
// requests everybody pays for.
const POLL_MS = 15000;

export function AdminTransactions() {
  const nav = useNavigate();
  // The message-count icons open straight on the Messages card, not the top of the case — a click
  // on "3 from customer" that lands on the status strip and makes admin scroll to find them isn't
  // what the icon promised. expandSection runs first: the card is collapsible, and a link landing
  // on a collapsed panel would show nothing (see CollapsibleSection.jsx::expandSection).
  const goToMessages = (t) => { expandSection("case-messages"); nav(`/admin/txn/${t.id}#case-messages`); };
  const [rows, setRows] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [msgFor, setMsgFor] = useState(null);
  const [msg, setMsg] = useState("");
  const [docFor, setDocFor] = useState(null); // txn being edited
  const [docState, setDocState] = useState(null); // { template, form_data, extra_clauses, body, admin_notes }
  const [deliveryFor, setDeliveryFor] = useState(null); // txn being edited
  const [deliveryState, setDeliveryState] = useState(null); // { delivery_status, courier_name, tracking_id, tracking_url }
  // For the delivery dialog's partner picker — same catalog, same fetch-once-on-mount pattern as
  // AdminTransactionDetail.jsx's own delivery dialog, so updating delivery from the queue offers
  // the same courier list as updating it from the case page.
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/admin/delivery-partners").then(r => setPartners(r.data)).catch(() => {}); }, []);
  const [closeDialogFor, setCloseDialogFor] = useState(null); // txn being closed
  const [closeNote, setCloseNote] = useState("");
  // Any modal being open suspends the background refresh — see the polling effect below.
  const dialogOpen = Boolean(msgFor || docFor || deliveryFor || closeDialogFor);
  // Search / tab / window / sort live in the query string, not just in component state: opening a
  // case unmounts this page, so plain state meant coming back landed on the unfiltered default list
  // and the id you had pasted into the search box was gone. Seeded from the URL on mount and
  // mirrored back into it (replace, so typing doesn't fill the history with one entry per
  // keystroke) — which also makes a filtered queue a shareable link.
  const [params, setParams] = useSearchParams();
  // Sticky across navigation (see lib/stickySearch.js), still seeded from `?q=` so a shared
  // link lands on the same filtered view. The URL wins on first load; after that the session
  // remembers, so opening a case and coming back does not lose the query.
  const [search, setSearch, clearSearch] = useStickySearch("admin-txn");
  useEffect(() => {
    const q = params.get("q");
    if (q) setSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sortField, setSortField] = useState(() => params.get("sort") || "modified");
  const [sortDir, setSortDir] = useState(() => (params.get("dir") === "asc" ? "asc" : "desc"));
  // **The URL is the queue.** Derived, not mirrored into state.
  //
  // This used to be `useState` seeded from `?tab=`, plus an effect copying the URL into it, while a
  // second effect copied it back out. Two effects each able to trigger the other is a loop, and it
  // was a real one: clicking a sidebar sub-item made the address bar oscillate `?tab=pending` ↔
  // `?tab=closed` around a hundred times, with the table redrawing on every flip, until the page
  // was reloaded. The guard that was meant to stop it compared against a `queue` captured in a
  // stale closure, so it compared the wrong value.
  //
  // Deriving removes the second copy and with it the possibility: there is one place the current
  // queue lives, and changing it is a navigation. The sidebar links, the dashboard cards and the
  // chips below all work by the same mechanism as a result.
  const queue = useMemo(() => {
    const t = params.get("tab");
    return TXN_QUEUES.some(q => q.key === t) ? t : DEFAULT_TXN_QUEUE;
  }, [params]);
  // The queue is always written to the URL, including the default. It used to be omitted when it
  // was the default, which made the address bar disagree with the sidebar — the menu links to
  // `?tab=pending` and clicking the Pending chip would have stripped it back off again.
  const setQueue = useCallback((key) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  }, [params, setParams]);
  // The date window. Seeded from `?from=&to=` — which is what a dashboard card click carries, so
  // arriving from a number lands on the same span that number was counted over — and otherwise
  // from `default_range_days` in Admin → Settings. Measured on **last activity** (`updated_at`,
  // falling back to `created_at`), the same clock these queues sort by, so a case opened months
  // ago but worked on yesterday stays visible.
  const { from, to, ready, setRange } = useDateRange(params);
  // Mirror the *view* into the URL — which queue, which window, which sort. Deliberately **not**
  // the search text.
  //
  // Writing `q` here made the page flicker. The sidebar's Transactions sub-items are plain links
  // carrying only `?tab=`, so every click dropped the search from the URL and this effect put it
  // straight back: two URL writes and two renders per click, which is the flashing. The search
  // already survives navigation in sessionStorage (lib/stickySearch.js), so the URL copy bought
  // nothing. `?q=` is still *read* on mount, so an existing shared link still lands filtered.
  //
  // The comparison is order-insensitive. It used to be `next.toString() !== params.toString()`,
  // which is an infinite loop waiting for a URL whose parameters are in a different order —
  // `?tab=closed&q=aff` can never equal a rebuilt `?q=aff&tab=closed`, so the effect would rewrite,
  // re-render and rewrite forever. That is the "it only stops if I refresh" symptom.
  useEffect(() => {
    // `tab` is not written here — it *is* the URL, and setQueue navigates. Rebuilding it from a
    // derived value would put this effect back in the loop it was just taken out of.
    const next = new URLSearchParams();
    if (params.get("tab")) next.set("tab", params.get("tab"));
    // Always written once resolved, not just when it differs from the default: the default lives
    // in Settings and can change, so a link that omits the window would not mean the same thing
    // tomorrow as it did when it was copied.
    if (ready) { next.set("from", from); next.set("to", to); }
    if (sortField !== "modified") next.set("sort", sortField);
    if (sortDir !== "desc") next.set("dir", sortDir);
    const norm = (u) => [...u.entries()].filter(([k]) => k !== "q").sort().map(kv => kv.join("=")).join("&");
    if (norm(next) !== norm(params)) setParams(next, { replace: true });
  }, [ready, from, to, sortField, sortDir, params, setParams]);
  const load = () => fetchAll("/admin/transactions").then(setRows).catch(() => {});
  const togglePin = usePinToggle("transaction", setRows);
  useEffect(() => { load(); api.get("/admin/reviewers").then(r => setReviewers(r.data)).catch(() => {}); }, []);

  // ---- live updates -------------------------------------------------------------------------
  // The queue used to change only on a manual refresh, so a case paid for or a review submitted
  // while the page sat open was simply invisible.
  //
  // It polls a deliberately tiny endpoint (`max(updated_at)` + `count`, one indexed row) and only
  // re-fetches the real list when that answer differs from what it already has. Polling the list
  // itself would re-run the most expensive query in the application every few seconds to discover
  // that usually nothing happened.
  //
  // Three things it refuses to do, each of which would make it worse than no refresh at all:
  //   * poll while the tab is hidden — an admin with this open in a background tab all day is not
  //     watching it, and the requests are pure waste;
  //   * refresh while a dialog is open — replacing the rows underneath someone mid-way through
  //     assigning a reviewer or writing a closing note is how you lose their work;
  //   * fire while a previous refresh is still in flight.
  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    const seen = { latest: undefined, total: undefined };

    const tick = async () => {
      if (stopped || inFlight || document.hidden || dialogOpen) return;
      inFlight = true;
      try {
        const { data } = await api.get("/admin/transactions/pulse");
        const changed = seen.latest !== undefined
          && (data.latest !== seen.latest || data.total !== seen.total);
        seen.latest = data.latest;
        seen.total = data.total;
        if (changed && !stopped) await load();
      } catch {
        // A failed poll is not worth surfacing: the page still shows the last good data, and the
        // next tick retries. Showing an error for a background refresh trains people to ignore
        // errors.
      } finally {
        inFlight = false;
      }
    };

    tick();                                   // establish the baseline immediately
    const id = setInterval(tick, POLL_MS);
    // Catch up the moment the tab comes back rather than waiting out the remaining interval.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };
  // Rows matching the search, before the date window is applied — the baseline the "older
  // transactions hidden" notice below is measured against.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    const accessor = ADMIN_TXN_SORT_ACCESSORS[sortField];
    return rows
      .filter(t => adminTxnMatchesSearch(t, q))
      // Pinned rows lead every queue; the chosen sort still orders each group (see lib.jsx).
      .sort(pinnedFirst((a, b) => {
        const av = accessor(a), bv = accessor(b);
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      }));
  }, [rows, search, sortField, sortDir]);

  // Every tab derives from `visible`, so the window applies to all of them at once. A pinned case
  // is exempt: pinning it was asking to keep it in view, and the quiet case that drifts out of the
  // window is the one that most needs to stay there.
  const inWindow = useCallback(
    (t) => t.pinned || withinRange(t.updated_at || t.created_at, from, to), [from, to]);
  const visible = useMemo(() => searched.filter(inWindow), [searched, inWindow]);

  // What the window is holding back, and — the part that matters — how much of it still has an
  // open action item. A stale case has an old last-activity date *by definition*, so a recency
  // filter hides precisely the work most likely to have been forgotten. The count is always shown
  // rather than letting rows disappear silently.
  const hidden = useMemo(() => {
    if (!ready || from === ALL_TIME) return { total: 0, actionable: 0 };
    const out = searched.filter(t => !inWindow(t));
    return { total: out.length, actionable: out.filter(t => (t.action_items || []).length > 0).length };
  }, [ready, from, searched, inWindow]);
  const openRows = useMemo(() => visible.filter(t => t.status !== "closed"), [visible]);
  const closedRows = useMemo(() => visible.filter(t => ["closed", "completed"].includes(t.status)), [visible]);
  // A case out with the courier is nobody's action until it lands, so it leaves the working
  // queues entirely and waits in Pending: Delivery.
  const inDelivery = (t) => t.delivery_status === "dispatched" && t.status === "submitted";
  const deliveryRows = useMemo(() => visible.filter(inDelivery), [visible]);
  const ownerRows = useMemo(() => ({
    customer: visible.filter(t => (t.action_items || []).some(i => i.owner === "customer")),
    admin: visible.filter(t => !inDelivery(t) && (t.action_items || []).some(i => i.owner === "admin")),
    reviewer: visible.filter(t => (t.action_items || []).some(i => i.owner === "reviewer")),
  }), [visible]);
  // `pending` is everything still owed by somebody — the union of the three owner queues and the
  // parcels out with a courier. It exists because the sidebar offers three destinations (All Open,
  // Pending, Completed) rather than six tabs, and "pending" as a *navigation* target has to mean
  // all of it. Which side owes it is a refinement made once you are looking at the list, so that
  // choice lives in the chips below rather than in the menu.
  //
  // Filtered from `visible` rather than concatenated from the four lists, so it keeps the one
  // sort order — pins first — instead of reading customer cases, then admin, then advocate.
  const pendingRows = useMemo(() => {
    const ids = new Set([...ownerRows.customer, ...ownerRows.admin, ...ownerRows.reviewer, ...deliveryRows]
      .map(t => t.id));
    return visible.filter(t => ids.has(t.id));
  }, [visible, ownerRows, deliveryRows]);
  const rowsByQueue = { open: openRows, closed: closedRows, pending: pendingRows,
                        delivery: deliveryRows, ...ownerRows };
  const displayRows = rowsByQueue[queue] || openRows;

  // Paginate the queue. This page renders the widest rows in the admin console and every queue
  // draws from the same fully-loaded list, so without a bound it renders every transaction that
  // has ever existed — five times over, once per queue tab.
  const pageSize = usePageSize();
  const pagedRows = usePaged(displayRows, pageSize);
  // Switching queue, searching, sorting or changing the date window all change what the list *is*.
  // Staying on page 3 of the previous list shows an empty table, which reads as "no results" for a
  // queue that has plenty.
  useEffect(() => { pagedRows.setPage(0); },
            [queue, search, sortField, sortDir, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/transactions/${id}/status`, { status });
      toast.success(status === "completed" ? "Marked completed" : `Status → ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
  };
  // closing is terminal (no reopen path) and irreversible in practice — route it through a
  // dialog that requires a note instead of a bare one-click select; see LIFECYCLE.md "Closing a case"
  const handleStatusChange = (t, v) => {
    if (v === "closed") { setCloseDialogFor(t); setCloseNote(""); }
    else setStatus(t.id, v);
  };
  const confirmClose = async () => {
    if (!closeNote.trim()) { toast.error("A closing note is required"); return; }
    try {
      await api.put(`/admin/transactions/${closeDialogFor.id}/status`, { status: "closed", note: closeNote.trim() });
      toast.success("Case closed");
      setCloseDialogFor(null); setCloseNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };
  const assign = async (id, reviewer_id) => { await api.put(`/admin/transactions/${id}/assign`, { reviewer_id }); toast.success("Assigned to advocate"); load(); };
  const claimSelf = async (id) => { await api.put(`/admin/transactions/${id}/review`, { reviewed_by: "admin" }); toast.success("You are now handling this case"); load(); };
  // two-way: admin can open the reviewer's comment channel and shut it again — see
  // LIFECYCLE.md "reviewer_channel_open"
  const setReviewerChannel = async (id, open) => {
    try {
      await api.put(`/admin/transactions/${id}/reviewer-channel`, { open });
      toast.success(open ? "Reviewer channel opened" : "Reviewer channel closed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const sendMsg = async () => { await api.post(`/admin/transactions/${msgFor}/quick-message`, { message: msg }); toast.success("Message sent"); setMsgFor(null); setMsg(""); };
  const refund = async (id) => {
    if (!window.confirm("Refund this payment?")) return;
    try { await api.put(`/admin/transactions/${id}/refund`); toast.success("Refunded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const openDelivery = (t) => {
    setDeliveryFor(t);
    setDeliveryState({
      delivery_status: t.delivery_status || "not_started",
      courier_name: t.courier_name || "", tracking_id: t.tracking_id || "", tracking_url: t.tracking_url || "",
      // Not resolved from courier_name — the stored fields are plain strings with no link back to
      // a catalog row, so reopening the dialog always starts in "manual" state even if a partner
      // was picked last time.
      partner_id: "",
    });
  };
  const saveDelivery = async () => {
    try {
      await api.put(`/admin/transactions/${deliveryFor.id}/delivery`, deliveryState);
      toast.success(`Delivery → ${deliveryState.delivery_status}`);
      setDeliveryFor(null); setDeliveryState(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const openDocEditor = async (t) => {
    setDocFor(t);
    try {
      const { data } = await api.get(`/admin/transactions/${t.id}/document`);
      const ov = data.overrides || {};
      setDocState({
        template: data.template,
        form_data: { ...(data.transaction?.form_data || {}), ...(ov.form_data || {}) },
        extra_clauses: ov.extra_clauses || "",
        body: ov.body || "",
        admin_notes: ov.admin_notes || "",
      });
    } catch (e) { toast.error("Could not load document"); setDocFor(null); }
  };
  const saveDocEdits = async () => {
    try {
      await api.put(`/admin/transactions/${docFor.id}/document`, {
        form_data: docState.form_data,
        extra_clauses: docState.extra_clauses,
        body: docState.body,
        admin_notes: docState.admin_notes,
      });
      toast.success("Saved edits");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const completeReview = async (id) => {
    try { await api.put(`/admin/transactions/${id}/review-status`, { review_status: "accepted" }); toast.success("Review completed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const renderHeader = (columns = ADMIN_TXN_COLUMNS, gridCols = ADMIN_TXN_GRID_COLS) => (
    <div className={`hidden md:grid ${gridCols} gap-2 px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase text-[#6B7280]`}>
      {columns.map(col => (
        <button key={col.key} data-testid={`admin-sort-${col.key}`} onClick={() => toggleSort(col.key)} className="flex items-center gap-1 hover:text-white transition-colors text-left">
          {col.label}
          {sortField === col.key
            ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
            : <ArrowUpDown className="w-3 h-3 opacity-40" />}
        </button>
      ))}
    </div>
  );

  const renderPendingCustomerRow = (t) => {
    const open = () => nav(`/admin/txn/${t.id}`);
    return (
      <div key={t.id} data-testid={`admin-txn-${t.id}`} className={`grid ${PENDING_CUSTOMER_GRID_COLS} gap-2 px-4 py-3 items-start hover:bg-white/[0.02] transition-colors ${unreadRowClass(t)}`}>
        <TxnIdCell t={t} onOpen={open} onTogglePin={() => togglePin(t)}/>
        <div className="text-xs text-[#A0AABF] pt-1.5 cursor-pointer" onClick={open}>{formatShortDate(t.created_at)}</div>
        <div className="text-xs text-[#A0AABF] pt-1.5 cursor-pointer" onClick={open} title={formatShortDate(t.updated_at || t.created_at)}>
          {formatRelativeTime(t.updated_at || t.created_at)}
        </div>
        <CustomerCell t={t} onOpen={open}/>
        <div className="pt-1.5 cursor-pointer" onClick={open}><StatusPill status={t.status} dim="status"/></div>
      </div>
    );
  };

  const renderPendingAdminRow = (t) => {
    // Every control below is gated on the same rules the server enforces (LIFECYCLE.md "Gates").
    // Assignment alone is NOT enough to issue a document — the review must be complete too,
    // including when admin is the reviewer.
    const g = txnGates(t);
    const issueBlock = gateReason(t, g, "issue");
    const dispatchBlock = gateReason(t, g, "dispatch");
    return (
      <div key={t.id} data-testid={`admin-txn-${t.id}`} className={`grid ${PENDING_ADMIN_GRID_COLS} gap-2 px-4 py-3 items-start hover:bg-white/[0.02] transition-colors ${unreadRowClass(t)}`}>
        {/* messages identify the case rather than gate it, so they sit with the ID */}
        <TxnIdCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)} onTogglePin={() => togglePin(t)}>
          <button data-testid={`msg-customer-${t.id}`} onClick={() => goToMessages(t)} title={`${t.customer_message_count || 0} from customer`} className={`inline-flex items-center gap-0.5 text-[10px] hover:underline ${msgIconClass(t.customer_message_count, t, "text-[#7DB2FB]")}`}>
            <MessageCircle className="w-3 h-3"/>{t.customer_message_count || 0}
          </button>
          <button data-testid={`msg-reviewer-${t.id}`} onClick={() => goToMessages(t)} title={`${t.reviewer_message_count || 0} from reviewer`} className={`inline-flex items-center gap-0.5 text-[10px] hover:underline ${msgIconClass(t.reviewer_message_count, t, "text-[#CBA96C]")}`}>
            <MessageSquare className="w-3 h-3"/>{t.reviewer_message_count || 0}
          </button>
        </TxnIdCell>
        <div className="text-xs text-[#A0AABF] pt-1.5 cursor-pointer" onClick={() => nav(`/admin/txn/${t.id}`)} title={formatShortDate(t.updated_at || t.created_at)}>{formatRelativeTime(t.updated_at || t.created_at)}</div>
        <CustomerCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)}/>
        {/* read-only: the workflow moves live in the Action menu, not a raw status dropdown */}
        <div className="pt-1.5"><StatusPill status={t.admin_stage} dim="admin_stage"/></div>
        <div className="text-xs text-[#A0AABF] pt-1.5">{formatPendingDuration(t.admin_pending_since || t.created_at)}</div>
        <div className="pt-1.5 flex items-center justify-between gap-1">
          <span className="text-xs text-white truncate">{t.reviewer_name || (t.reviewed_by === "admin" ? "You (admin)" : "Unassigned")}</span>
          <AssignMenu t={t} reviewers={reviewers} onClaimSelf={claimSelf} onAssign={assign}/>
        </div>
        <div className="pt-1 flex items-center justify-between gap-1">
          <StatusPill status={t.review_status} dim="review_status"/>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`action-menu-${t.id}`} aria-label="Case actions" className="p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors shrink-0">
                <MoreVertical className="w-3.5 h-3.5"/>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white w-60">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6B7280]">Review</DropdownMenuLabel>
              {/* No "Under review" item: the Status column already reports that, and admin
                  self-assigning starts the review automatically. */}
              <DropdownMenuItem data-testid={`act-review-${t.id}`} disabled={!g.canCompleteReview}
                onClick={() => completeReview(t.id)} className="cursor-pointer focus:bg-white/5 focus:text-white">
                Review completed
                {!g.canCompleteReview && (
                  <span className="ml-auto text-[10px] text-[#6B7280]">
                    {g.reviewDone ? "done" : !g.assigned ? "assign first" : "awaiting advocate"}
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10"/>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6B7280]">Finish</DropdownMenuLabel>
              <DropdownMenuItem data-testid={`act-complete-${t.id}`} disabled={!g.canComplete}
                onClick={() => setStatus(t.id, "completed")} className="cursor-pointer focus:bg-white/5 focus:text-white">
                Txn completed
                {!g.canComplete && <span className="ml-auto text-[10px] text-[#6B7280]">nothing issued yet</span>}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`act-close-${t.id}`} disabled={!g.canClose}
                onClick={() => handleStatusChange(t, "closed")} className="cursor-pointer focus:bg-white/5 focus:text-white">
                Closed (with notes)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Both fulfillment channels as colour-coded icons — green done, red not yet, amber in
            flight. Same actions as the old per-column menus, a third of the width. */}
        <div className="pt-1 flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`softcopy-icon-${t.id}`} aria-label="Soft copy actions"
                title={`Soft copy: ${pillLabel(t.softcopy_status, "softcopy_status")}`}
                className={`p-1.5 rounded-md hover:bg-white/10 transition-colors ${softcopyIconTone(t.softcopy_status)}`}
              ><FileText className="w-4 h-4"/></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6B7280]">
                Soft copy — {pillLabel(t.softcopy_status, "softcopy_status")}
              </DropdownMenuLabel>
              <DropdownMenuItem data-testid={`act-softcopy-${t.id}`} disabled={!g.canIssue}
                onClick={() => document.getElementById(`admin-softcopy-file-${t.id}`).click()} className="cursor-pointer focus:bg-white/5 focus:text-white">
                {t.softcopy_status === "available" ? "Overwrite soft copy" : "Upload soft copy"}
                {issueBlock && <span className="ml-2 text-[10px] text-[#6B7280]">— {issueBlock}</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            id={`admin-softcopy-file-${t.id}`} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.gif,.doc,.docx,.rtf,.txt,.zip"
            onChange={async (e) => {
              const f = e.target.files[0]; if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              try { await api.post(`/admin/transactions/${t.id}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Soft copy uploaded + emailed"); load(); }
              catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
            }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`delivery-icon-${t.id}`} aria-label="Delivery actions"
                title={`Delivery: ${pillLabel(t.delivery_status, "delivery_status")}${t.tracking_id ? ` · ${t.courier_name || ""} ${t.tracking_id}` : ""}`}
                className={`p-1.5 rounded-md hover:bg-white/10 transition-colors ${deliveryIconTone(t.delivery_status)}`}
              ><Truck className="w-4 h-4"/></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111623] border-white/10 text-white">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6B7280]">
                Delivery — {pillLabel(t.delivery_status, "delivery_status")}
              </DropdownMenuLabel>
              <DropdownMenuItem data-testid={`act-delivery-${t.id}`}
                disabled={t.delivery_status === "not_started" && !g.canDispatch}
                onClick={() => openDelivery(t)} className="cursor-pointer focus:bg-white/5 focus:text-white">
                Update delivery — courier &amp; customer
                {t.delivery_status === "not_started" && dispatchBlock && <span className="ml-2 text-[10px] text-[#6B7280]">— {dispatchBlock}</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const renderPendingReviewerRow = (t) => {
    const reviewerItems = (t.action_items || []).filter(i => i.owner === "reviewer");
    return (
      <div key={t.id} data-testid={`admin-txn-${t.id}`} className={`grid ${PENDING_REVIEWER_GRID_COLS} gap-2 px-4 py-3 items-start hover:bg-white/[0.02] transition-colors ${unreadRowClass(t)}`}>
        <TxnIdCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)} onTogglePin={() => togglePin(t)}/>
        <div className="text-xs text-[#A0AABF] pt-1.5 cursor-pointer" onClick={() => nav(`/admin/txn/${t.id}`)} title={formatShortDate(t.updated_at || t.created_at)}>{formatRelativeTime(t.updated_at || t.created_at)}</div>
        <CustomerCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)}/>
        <div className="pt-1 flex items-center justify-between gap-1">
          <span className="text-xs text-white truncate">{t.reviewer_name || "Unassigned"}</span>
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid={`msg-reviewer-btn-${t.id}`} title="Message reviewer" onClick={()=>setMsgFor(t.id)} className="p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors">
              <MessageSquare className="w-3.5 h-3.5"/>
            </button>
            <AssignMenu t={t} reviewers={reviewers} onClaimSelf={claimSelf} onAssign={assign}/>
          </div>
        </div>
        <div className="pt-1.5">
          {reviewerItems.length > 0 ? <ActionBadges items={reviewerItems}/> : <span className="text-xs text-[#6B7280]">—</span>}
        </div>
        <div className="text-xs text-[#A0AABF] pt-1.5">{formatPendingDuration(t.admin_pending_since || t.created_at)}</div>
        <div className="pt-1.5 flex items-center gap-3">
          <button onClick={() => goToMessages(t)} title="Messages from customer" className={`inline-flex items-center gap-1 text-xs hover:underline ${msgIconClass(t.customer_message_count, t, "text-[#7DB2FB]")}`}>
            <MessageCircle className="w-3.5 h-3.5"/>{t.customer_message_count || 0}
          </button>
          <button onClick={() => goToMessages(t)} title="Messages from reviewer" className={`inline-flex items-center gap-1 text-xs hover:underline ${msgIconClass(t.reviewer_message_count, t, "text-[#CBA96C]")}`}>
            <MessageSquare className="w-3.5 h-3.5"/>{t.reviewer_message_count || 0}
          </button>
        </div>
      </div>
    );
  };

  const renderCompletedRow = (t) => {
    const note = getClosingNote(t);
    return (
      <div key={t.id} data-testid={`admin-txn-${t.id}`} onClick={() => nav(`/admin/txn/${t.id}`)} className={`grid ${COMPLETED_GRID_COLS} gap-2 px-4 py-3 items-start hover:bg-white/[0.02] transition-colors ${unreadRowClass(t)} cursor-pointer`}>
        <TxnIdCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)} onTogglePin={() => togglePin(t)}/>
        <div className="text-xs text-[#A0AABF] pt-1.5" title={formatShortDate(t.created_at)}>{formatShortDate(t.created_at)}</div>
        <div className="text-xs text-[#A0AABF] pt-1.5" title={formatShortDate(t.updated_at || t.created_at)}>{formatShortDate(t.updated_at || t.created_at)}</div>
        <div className="min-w-0 pt-1.5">
          <div className="text-sm text-white truncate">{t.template_title || t.service_type}</div>
          <div className="text-xs text-[#6B7280] truncate">{t.customer_name}</div>
        </div>
        <div className="pt-1.5"><StatusPill status={t.payment_status} dim="payment_status"/></div>
        <InvoiceCell t={t}/>
        <div className="text-xs text-[#A0AABF] pt-1.5 truncate" title={note || ""}>
          {note || <span className="text-[#6B7280] italic">No note on record</span>}
        </div>
      </div>
    );
  };

  const renderRow = (t) => {
    // same gates the server enforces (LIFECYCLE.md "Gates") — assignment alone does not unlock
    // fulfillment; the review has to be complete too
    const g = txnGates(t);
    const issueBlock = gateReason(t, g, "issue");
    const dispatchBlock = gateReason(t, g, "dispatch");
    return (
    <div key={t.id} data-testid={`admin-txn-${t.id}`} className={`grid ${ADMIN_TXN_GRID_COLS} gap-2 px-4 py-3 items-start hover:bg-white/[0.02] transition-colors ${unreadRowClass(t)} text-[13px]`}>
      <TxnIdCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)} onTogglePin={() => togglePin(t)}>
        {t.admin_modified && <span className="text-[9px] text-[#CBA96C]">edited</span>}
      </TxnIdCell>
      <div className="text-[11px] text-[#A0AABF] pt-1 cursor-pointer" onClick={() => nav(`/admin/txn/${t.id}`)}>{formatShortDate(t.created_at)}</div>
      <div className="text-[11px] text-[#A0AABF] pt-1 cursor-pointer" onClick={() => nav(`/admin/txn/${t.id}`)} title={formatShortDate(t.updated_at || t.created_at)}>{formatRelativeTime(t.updated_at || t.created_at)}</div>
      <CustomerCell t={t} onOpen={() => nav(`/admin/txn/${t.id}`)}/>
      {/* Read-only. All Open is for triage — the workflow moves live on the case detail page and
          in the Pending: Admin queue, which is where admin actually works a case. Offering ten
          half-controls per row here made the list something to operate rather than scan. */}
      <div className="pt-1.5"><StatusPill status={t.admin_stage || t.status} dim={t.admin_stage ? "admin_stage" : "status"}/></div>
      <div className="pt-1.5"><PendingAtCell t={t}/></div>
    </div>
    );
  };

  // Every queue except "open" gets a purpose-built compact view; "open" stays the full table
  // since it's the one place admin needs every field/control at once (spans every stage).
  const QUEUE_VIEWS = {
    customer: { columns: PENDING_CUSTOMER_COLUMNS, gridCols: PENDING_CUSTOMER_GRID_COLS, render: renderPendingCustomerRow },
    admin: { columns: PENDING_ADMIN_COLUMNS, gridCols: PENDING_ADMIN_GRID_COLS, render: renderPendingAdminRow },
    reviewer: { columns: PENDING_REVIEWER_COLUMNS, gridCols: PENDING_REVIEWER_GRID_COLS, render: renderPendingReviewerRow },
    // same shape as Pending: Admin — the delivery icon and Action menu are exactly what admin
    // needs to record the outcome when the parcel lands
    delivery: { columns: PENDING_ADMIN_COLUMNS, gridCols: PENDING_ADMIN_GRID_COLS, render: renderPendingAdminRow },
    closed: { columns: COMPLETED_COLUMNS, gridCols: COMPLETED_GRID_COLS, render: renderCompletedRow },
  };
  const activeView = QUEUE_VIEWS[queue];

  return (
    <div className="space-y-10" data-testid="admin-txn-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="font-serif-display text-3xl md:text-4xl text-white">Transaction</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <DateRange from={from} to={to} onChange={setRange} testid="admin-txn-range" />
          <div className="relative flex-1 sm:w-72">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid="admin-txn-search" value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") setSearch(""); }}
              placeholder="Search txn, customer, status…"
              className="w-full bg-transparent border border-white/15 rounded-full pl-9 pr-9 py-2 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#CBA96C]/50"
            />
            {/* Clearing a pasted transaction ID by hand is 36 backspaces. Escape does it too. */}
            {search && (
              <button
                data-testid="admin-txn-search-clear" type="button" aria-label="Clear search"
                onClick={() => setSearch("")} title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#A0AABF] hover:text-white hover:bg-white/10 transition-colors"
              ><X className="w-3.5 h-3.5" /></button>
            )}
          </div>
        </div>
      </div>

      <section className="space-y-3" data-testid="admin-txn-section">
        {/* No top-level tab row. All Open / Pending / Completed are sidebar sub-items now, so the
            page has one job — show the queue it was sent to — instead of carrying a second
            navigation control that duplicated the menu beside it.

            Inside Pending, these chips narrow by who owes the next move. That is a refinement of
            the list you are already looking at, not a destination, which is why it appears here and
            only here. */}
        {["pending", "customer", "admin", "reviewer", "delivery"].includes(queue) && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="admin-txn-pending-filter">
            {[
              { key: "pending", label: "All pending", rows: pendingRows },
              { key: "customer", label: "Customer", rows: ownerRows.customer },
              { key: "admin", label: "Admin", rows: ownerRows.admin },
              { key: "reviewer", label: "Advocate", rows: ownerRows.reviewer },
              { key: "delivery", label: "With courier", rows: deliveryRows },
            ].map(f => (
              <button
                key={f.key} data-testid={`pending-filter-${f.key}`}
                onClick={() => setQueue(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors border ${
                  queue === f.key
                    ? "bg-[#CBA96C] text-[#080C16] border-[#CBA96C] font-medium"
                    : "border-white/15 text-[#A0AABF] hover:text-white hover:border-white/30"
                }`}
              >
                {f.label} ({f.rows.length})
              </button>
            ))}
          </div>
        )}

        {/* Nothing disappears silently. A stale case has an old last-activity date by definition,
            so the recency window hides exactly the work most likely to have been forgotten —
            calling that out (loudly, when any of it is still actionable) is what makes a default
            window safe to have at all. */}
        {hidden.total > 0 && (
          <div
            data-testid="admin-txn-hidden-notice"
            className={`flex items-center justify-between gap-3 flex-wrap rounded-xl border px-4 py-2.5 text-xs ${
              hidden.actionable > 0
                ? "border-[#F5A524]/40 bg-[#F5A524]/10 text-[#F5A524]"
                : "border-white/10 bg-white/[0.02] text-[#6B7280]"
            }`}
          >
            <span>
              {hidden.total} older transaction{hidden.total === 1 ? "" : "s"} hidden by the{" "}
              {rangeLabel(from, to)} filter
              {hidden.actionable > 0 && (
                <strong> — {hidden.actionable} still {hidden.actionable === 1 ? "has" : "have"} an open action item</strong>
              )}
            </span>
            <button
              data-testid="admin-txn-show-all"
              onClick={() => setRange({ from: ALL_TIME, to: isoToday() })}
              className="underline underline-offset-2 hover:opacity-80 shrink-0"
            >Show all time</button>
          </div>
        )}

        <div className="bg-[#111623] border border-white/10 rounded-2xl divide-y divide-white/5 overflow-x-auto">
          <Pagination {...pagedRows} testid="admin-txn-pager" />
          {activeView ? renderHeader(activeView.columns, activeView.gridCols) : renderHeader()}
          {rows.length === 0 && (
            <div className="text-center text-[#6B7280] py-10 text-sm">No transactions yet.</div>
          )}
          {rows.length > 0 && displayRows.length === 0 && (
            <div className="text-center text-[#6B7280] py-10 text-sm">
              {search
                ? `No transactions match "${search}".`
                : hidden.total > 0
                  ? `Nothing in this queue in the ${rangeLabel(from, to)}.`
                  : "Nothing in this queue."}
            </div>
          )}
          {pagedRows.visible.map(activeView ? activeView.render : renderRow)}
        </div>
      </section>

      <Dialog open={!!msgFor} onOpenChange={(o)=>!o&&setMsgFor(null)}>
        <DialogContent><DialogHeader><DialogTitle>Quick Message to Reviewer</DialogTitle></DialogHeader>
          <textarea data-testid="quick-msg-text" value={msg} onChange={e=>setMsg(e.target.value)} rows={4} className="w-full border rounded p-2"/>
          <Button data-testid="quick-msg-send" onClick={sendMsg} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Send</Button>
        </DialogContent>
      </Dialog>

      {/* Delivery tracking — courier_name/tracking_id/tracking_url are here so a delivery-partner
          webhook can populate the same fields once that integration lands */}
      <Dialog open={!!deliveryFor} onOpenChange={(o)=>!o && (setDeliveryFor(null), setDeliveryState(null))}>
        <DialogContent data-testid="delivery-dialog">
          <DialogHeader><DialogTitle>Delivery — {deliveryFor?.customer_name}</DialogTitle></DialogHeader>
          {deliveryState && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[#6B7280]">Status</Label>
                <Select value={deliveryState.delivery_status} onValueChange={(v)=>setDeliveryState(s=>({...s, delivery_status: v}))}>
                  <SelectTrigger data-testid="delivery-status-select" className="mt-1 h-9 text-sm"><SelectValue/></SelectTrigger>
                  <SelectContent>{nextStates("delivery_status", deliveryFor?.delivery_status || "not_started").map(s => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {partners.length > 0 && (
                <div>
                  <Label className="text-xs text-[#6B7280]">Delivery partner</Label>
                  {/* A convenience, not a link the record depends on: picking one just pre-fills
                      courier name + tracking URL below from Settings → Delivery partners, and
                      every field stays directly editable afterward — the stored shape
                      (courier_name/tracking_id/tracking_url as plain strings) never changes. */}
                  <Select value={deliveryState.partner_id || ""} onValueChange={(pid) => {
                    const partner = partners.find(p => p.id === pid);
                    setDeliveryState(s => ({
                      ...s, partner_id: pid,
                      courier_name: partner ? partner.name : s.courier_name,
                      tracking_url: partner ? buildTrackingUrl(partner.url_template, s.tracking_id) : s.tracking_url,
                    }));
                  }}>
                    <SelectTrigger data-testid="delivery-partner" className="mt-1 h-9 text-sm"><SelectValue placeholder="Type it in manually below, or pick one…"/></SelectTrigger>
                    <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-[#6B7280]">Courier name</Label>
                <Input data-testid="delivery-courier" value={deliveryState.courier_name} onChange={e=>setDeliveryState(s=>({...s, courier_name: e.target.value}))} className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Tracking ID</Label>
                {/* Recomputes the URL below on every keystroke when a partner is selected — typing
                    the id is usually the last thing admin does, and re-deriving the URL from it is
                    what actually makes picking a partner save any work. */}
                <Input data-testid="delivery-tracking-id" value={deliveryState.tracking_id} onChange={e => {
                  const tracking_id = e.target.value;
                  const partner = partners.find(p => p.id === deliveryState.partner_id);
                  setDeliveryState(s => ({
                    ...s, tracking_id,
                    tracking_url: partner ? buildTrackingUrl(partner.url_template, tracking_id) : s.tracking_url,
                  }));
                }} className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Tracking URL</Label>
                <Input data-testid="delivery-tracking-url" value={deliveryState.tracking_url} onChange={e=>setDeliveryState(s=>({...s, tracking_url: e.target.value}))} className="mt-1 h-9 text-sm"/>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <Button data-testid="delivery-save" onClick={saveDelivery} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Closing is terminal (no reopen path) and irreversible in practice — require a note
          instead of a bare one-click select; see LIFECYCLE.md "Closing a case" */}
      <Dialog open={!!closeDialogFor} onOpenChange={(o)=>!o && (setCloseDialogFor(null), setCloseNote(""))}>
        <DialogContent data-testid="close-dialog">
          <DialogHeader><DialogTitle>Close case — {closeDialogFor?.customer_name}</DialogTitle></DialogHeader>
          {closeDialogFor && (
            <div className="space-y-3">
              {closeDialogFor.softcopy_status !== "available" && closeDialogFor.delivery_status !== "delivered" && (
                <div className="text-xs text-[#F5A524] bg-[#F5A524]/10 border border-[#F5A524]/40 rounded p-2">
                  Neither soft copy nor delivery is complete for this case yet. Closing is still allowed — just make sure that's intentional.
                </div>
              )}
              <div>
                <Label className="text-xs text-[#6B7280]">Closing note (required)</Label>
                <Textarea data-testid="close-note-input" rows={3} value={closeNote} onChange={e=>setCloseNote(e.target.value)} placeholder="Why is this case being closed?" className="mt-1"/>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={()=>{setCloseDialogFor(null); setCloseNote("");}}>Cancel</Button>
                <Button data-testid="close-confirm" disabled={!closeNote.trim()} onClick={confirmClose} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium">Close Case</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document editor */}
      <Dialog open={!!docFor} onOpenChange={(o)=>!o && (setDocFor(null), setDocState(null))}>
        <DialogContent data-testid="doc-editor-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Customise document — {docFor?.customer_name} · {docFor?.service_type}{docFor?.subtype && ` · ${docFor.subtype}`}</DialogTitle></DialogHeader>
          {docState && (
            <div className="space-y-5">
              <div>
                <div className="text-xs font-medium text-[#A0AABF] mb-2">Customer form fields</div>
                <div className="grid grid-cols-2 gap-3 max-h-[240px] overflow-y-auto pr-2 border rounded p-3 bg-white/5">
                  {(docState.template?.fields?.length ? docState.template.fields : Object.keys(docState.form_data || {})).map(f => (
                    <div key={f}>
                      <Label className="text-xs text-[#6B7280]">{f.replace(/_/g,' ')}</Label>
                      <Input data-testid={`doc-field-${f}`} value={docState.form_data[f] || ""}
                             onChange={e => setDocState(s => ({...s, form_data: {...s.form_data, [f]: e.target.value}}))}
                             className="mt-1 h-8 text-sm"/>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Extra clauses (appended to the customer's document)</Label>
                <Textarea data-testid="doc-extra-clauses" rows={4} value={docState.extra_clauses}
                          onChange={e => setDocState(s => ({...s, extra_clauses: e.target.value}))}
                          placeholder="e.g. The parties agree to waive notice of arbitration proceedings…"/>
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-[#A0AABF]">Full body override (advanced) — supports {'{{field}}'} substitution</summary>
                <Textarea data-testid="doc-body-override" rows={8} value={docState.body}
                          onChange={e => setDocState(s => ({...s, body: e.target.value}))}
                          className="mt-2 font-mono text-xs"
                          placeholder={"THIS AGREEMENT is made on {{lease_start}} between {{landlord_name}} …"}/>
              </details>
              <div>
                <Label className="text-xs text-[#6B7280]">Internal admin notes (not printed)</Label>
                <Textarea data-testid="doc-admin-notes" rows={2} value={docState.admin_notes}
                          onChange={e => setDocState(s => ({...s, admin_notes: e.target.value}))}/>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                {/* Saving edits no longer issues anything. The soft copy is the executed document
                    admin uploads on the case page; this editor shapes what the customer reads on
                    screen and what a draft print-out says, which is a different artifact. */}
                <Button onClick={saveDocEdits} data-testid="doc-save"
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium">Save Edits</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// One message bubble inside an admin relay pane. `pane` is the side whose thread we're looking
// at, and the control always answers the same question: *is this message visible to this pane's
// party?* So a message written by that party carries no control (they always see their own), and
// everything else — the other spoke's messages, and admin replies addressed elsewhere — can be
// toggled into or out of this thread.
